import axios from 'axios'
import { Network } from '@capacitor/network'
import { Capacitor } from '@capacitor/core'
import { storage } from './preferences'
import { i18n } from '@/i18n'

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete'])

const client = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  headers: {
    Accept: 'application/json',
  },
})

client.interceptors.request.use(async (config) => {
  const token = await storage.get('pavemi_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Block write requests when offline on native platforms.
// Reads are allowed through — they may be cached or benign to attempt.
client.interceptors.request.use(async (config) => {
  if (Capacitor.isNativePlatform()) {
    const method = (config.method || '').toLowerCase()
    if (WRITE_METHODS.has(method)) {
      try {
        const { connected } = await Network.getStatus()
        if (!connected) {
          return Promise.reject(new axios.Cancel('OFFLINE'))
        }
      } catch (_) {
        // Network plugin failure → allow request to proceed
      }
    }
  }
  return config
})

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 1. OFFLINE cancel → manejado por OfflineBanner
    if (axios.isCancel(error)) {
      if (error.message === 'OFFLINE' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('pavemi:offline-blocked'))
      }
      return Promise.reject(error)
    }

    const status = error.response?.status
    const config = error.config || {}

    // 2. Status ya manejados por otros sistemas
    if (status === 401) {
      await storage.remove('pavemi_token')
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }
    if (status === 402) {
      const { useUpgradeStore } = await import('@/stores/upgrade')
      useUpgradeStore().openFrom402(error.response.data)
      return Promise.reject(error)
    }

    // 3. Opt-out: la vista dijo "yo me hago cargo"
    if (config.silent === true) return Promise.reject(error)

    // 4. Toastear según tipo
    const { useToastStore } = await import('@/stores/toast')
    const toast = useToastStore()

    const { t } = i18n.global
    if (!error.response) {
      toast.error(t('errors.network'))
    } else if (status >= 500) {
      toast.error(t('errors.server_500'))
    } else if (status === 403) {
      toast.error(error.response.data?.message || t('errors.forbidden'))
    } else if (status === 404) {
      toast.error(error.response.data?.message || t('errors.not_found'))
    } else if (status === 422) {
      const errors = error.response.data?.errors
      const first = errors ? Object.values(errors).flat()[0] : null
      toast.error(first || error.response.data?.message || t('errors.validation'))
    } else if (status === 429) {
      toast.warn(t('errors.rate_limit'))
    } else {
      toast.error(error.response.data?.message || t('errors.generic'))
    }

    return Promise.reject(error)
  },
)

export default client
